import ErrorMessage from "components/common/ErrorMessage";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import ListTable from "components/dashboard/ListTable";
import { fetchDashboardAppointments } from "slices/dashboardSlice";
import { DashboardSelector } from "slices/dashboardSlice";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import { usePermissions } from "layouts/Admin";
import { LegendButton } from "components/common/TableColor";
import { legends } from "components/common/TableColor";
import { fetchDashboardAnalytics } from "slices/dashboardSlice";
import { fetchInterpreters } from "slices/interpreterSlice";
import { fetchJobLocations } from "slices/jobLocationSlice";
import { interpreterName } from "helpers/interpreterName";
import { DEFAULT_LIST_QUERY, mergeQuery, readListState, writeListState, clearListState, pickFormValues } from "helpers/listQuery";

const DASHBOARD_KEY = 'admin-dashboard';
// Whitelist, NOT the whole query: page/pageLength/sortBy must never become form values, because
// onSubmit spreads form data into the patch and a stale page would survive mergeQuery's reset.
const DASHBOARD_FORM_KEYS = [
  'status', 'start_date', 'end_date', 'case_number', 'appointment_id',
  'job_location_id', 'language_id', 'service', 'interpreter_ids',
];


const DatesInputs = (({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>Start Date*</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            <div className="input-image basic_bases">
              <input ref={startDateRef} id='start_date' type="date" name='start_date' placeholder='Start Date'
                {...field}
                value={field.value ? moment(field.value).format('YYYY-MM-DD') : ''}
                onChange={(e) => {
                  const dateString = e.target.value ? moment(e.target.value).format("YYYY-MM-DD") : ""
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />

            </div>
          } />
      </div>
      <div className="row-primary">
        <h2>End Date</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            <div className="input-image basic_bases">
              <input ref={endDateRef} type="date" name='end_date' placeholder='End Date'
                {...field}
                value={field.value ? moment(field.value).format('YYYY-MM-DD') : ''}
                onChange={(e) => {
                  const dateString = e.target.value ? moment(e.target.value).format("YYYY-MM-DD") : ""
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
      </div>
    </>
  )
});

function Dashboard() {
  const dispatch = useDispatch()
  const { dashboard, last_page: totalPages, loader, total } = useSelector(DashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const defaultValues = {
    status: 'all',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: '', // blank by default — no upper bound on the job search
    case_number: '',
  };

  // Opening a job unmounts this page, so the filters the user applied are read back from the
  // in-memory screen cache when they return. A refresh drops the whole JS bundle and therefore
  // the cache, which is the client's rule: intact on return, reset on refresh, never in local
  // storage (see helpers/listQuery).
  const cached = React.useRef(readListState(DASHBOARD_KEY)).current;

  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors, reset, } = useForm({
    defaultValues: cached
      ? { ...defaultValues, ...pickFormValues(cached.query, DASHBOARD_FORM_KEYS) }
      : defaultValues
  });
  // Status and the highlighted legend chip are DERIVED from the restored query, never read back
  // from the ui bag: a tile click calls setStatusValue and applyQuery in the same tick, so the ui
  // bag would still hold the previous status. Seeding a stale statusValue would then let the next
  // Search overwrite the applied status (onSubmit sends `status: statusValue`).
  const restoredStatus = cached?.query?.status ?? 'all';
  const [activeLegend, setActiveLegend] = useState(
    () => legends.find((legend) => legend.value === restoredStatus)?.id ?? 0
  );
  const [statusValue, setStatusValue] = React.useState(restoredStatus);
  const [interpreterState, setInterpreterState] = useState(cached?.ui?.interpreters ?? [])
  const [filterJobLocation, setFilterJobLocation] = useState(cached?.ui?.jobLocation ?? null);
  // The Language select needs the chosen {value,label} object, exactly like Job Location above:
  // the dashboard only ever loads languages through AsyncPaginate's own loader (which returns the
  // rows instead of filling the languages slice), so deriving the label from that slice leaves the
  // box blank while a language IS applied.
  const [filterLanguage, setFilterLanguage] = useState(cached?.ui?.language ?? null);

  // ONE query object for the whole screen. Every interaction — search, sort, page, page length,
  // legend chip, analytics tile, Search, Reset — goes through applyQuery, which merges a patch
  // into it (helpers/listQuery). That is what stops a sort from wiping the date range or a page
  // change from reverting the page size to 10.
  // pristineQuery is what Reset goes back to; initialQuery is what THIS mount starts from. They
  // differ after a restore, and conflating them would make Reset snap back to the restored
  // filters instead of the defaults.
  const pristineQuery = { ...DEFAULT_LIST_QUERY, ...defaultValues };
  const initialQuery = cached?.query ?? pristineQuery;
  const [query, setQuery] = useState(initialQuery);
  const queryRef = useRef(initialQuery);

  // Only the {value,label} objects the selects need to render their labels — everything else is
  // already in the query. These are set by a select's own onChange, a render before the Search
  // that applies them, so this ref is never stale at applyQuery time.
  const uiRef = useRef({});
  useEffect(() => {
    uiRef.current = {
      interpreters: interpreterState,
      jobLocation: filterJobLocation,
      language: filterLanguage,
    };
  }, [interpreterState, filterJobLocation, filterLanguage]);

  const applyQuery = React.useCallback((patch) => {
    const next = mergeQuery(queryRef.current, patch);
    queryRef.current = next;
    setQuery(next);
    // Only applyQuery writes the cache, so it can hold nothing but APPLIED state — a restored
    // form and a restored list can never disagree.
    writeListState(DASHBOARD_KEY, { query: next, ui: uiRef.current });
    dispatch(fetchDashboardAppointments(next.page, next));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchDashboardAnalytics())
    applyQuery({})
  }, [])

  // #region JonLocations dropdown 

  const filteredJobLocationLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchJobLocations(page, { pageLength: 20, sortBy: "id", sortDirection: "desc", search: inputValue, status: "active" }, true, true)
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.name,
      })),

      hasMore: page !== result?.last_page,
      additional: {
        page: page + 1,
      },
    };
  };

  //#endregion

  // #region Language dropdown 
  const filteredLanguages = React.useMemo(() =>
    languages?.map(language => {
      const object = {
        value: language?.id,
        label: language?.name
      }
      return object
    }), [languages]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "desc", search: inputValue, status: "active" }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  // #region Interpreters dropdown
  const filteredInterpretersLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchInterpreters(page, { pageLength: 20, sortBy: "id", sortDirection: "desc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: interpreterName(item),
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  // #endregion

  // #region Legends
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...legends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [legends]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    setStatusValue(value);
    setValue("status", value);
    applyQuery({ status: value });
  }
  //#endregion

  const onSubmit = (data) => {
    dispatch(setError({}))
    // `service` comes from the form (an analytics tile writes it there) instead of being
    // hardcoded to 'all', which used to discard an In Person / VRI tile selection.
    applyQuery({ ...data, status: statusValue });
    clearErrors();
  };

  const handleResetFields = () => {
    setValue("start_date", moment().format('YYYY-MM-DD'));
    setValue("end_date", "");
    setValue("language_id", "");
    setValue("appointment_id", "");
    setValue("case_number", "");
    setValue("job_location_id", "");
    setValue("interpreter_ids", []);
    setInterpreterState([]);
    setFilterJobLocation(null);
    setFilterLanguage(null);
    setValue("service", "");
    setValue("status", "all");
    setStatusValue('all');
    setActiveLegend(0);
    // Assign the whole query rather than patching it, so leftover keys (search text, a tile's
    // service, an old sort) cannot survive a Reset.
    queryRef.current = pristineQuery;
    setQuery(pristineQuery);
    // Forget the remembered filters too, or Reset would clear the screen and then the old
    // filters would come back the next time this page is opened.
    clearListState(DASHBOARD_KEY);
    dispatch(fetchDashboardAppointments(1, pristineQuery));
    clearErrors()
  };

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen, status = 'all') => {
    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
    const next_fourteen_days = moment().add(14, 'days').format('YYYY-MM-DD');
    if (isforteen == true) {
      setValue("start_date", tomorrow);
      setValue("end_date", next_fourteen_days);
    } else {
      setValue("start_date", today);
      setValue("end_date", today);
    }

    setValue("service", item?.value || null);
    setValue("status", status);
    setStatusValue(status);
    setActiveLegend(0);

    // A patch, not a replacement: the tile sets its own window/service/status and leaves the
    // user's page size, sort and search alone. It also lands in `query`, so the next sort or
    // page change keeps this date range instead of resurrecting older jobs.
    applyQuery({
      start_date: isforteen ? tomorrow : today,
      end_date: isforteen ? next_fourteen_days : today,
      service: item?.value || "",
      status,
    });

    tableRef.current.scrollIntoView({ behavior: "smooth" });
    dispatch(setError({}));
    clearErrors();
  };


  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          {/* ===== TODAY ===== */}
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'all'); }}>{service?.type}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'registered'); }}>{service?.registered}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== NEXT 14 DAYS ===== */}
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'all'); }}>{service?.type}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'registered'); }}>{service?.registered}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton activeId={activeLegend} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />

              <div className="row-primary">
                <h2>Interpreters</h2>
                <Controller
                  name="interpreter_ids"
                  control={control}
                  render={({ field }) => (
                    <div className="w-100">
                      <div className="w-100">
                        <AsyncPaginate
                          menuPlacement="auto"
                          isMulti
                          styles={{
                            ...selectCustomStyles,
                          }}
                          value={interpreterState}
                          loadOptions={filteredInterpretersLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(selected) => {
                            setInterpreterState(selected);
                            field.onChange(selected.map((opt) => opt.value));
                          }}
                          additional={{
                            page: 1,
                          }}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Job ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis basic_bases">
                      <input className="input-in type_inp" name="appointment_id" type="text" placeholder="e.g. A1235" {...field} />
                    </div>
                  }
                />
              </div>

              <div className="row-primary">
                <h2>Case Number</h2>
                <Controller
                  name="case_number"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis basic_bases">
                      <input className="input-in type_inp" name="case_number" type="text" placeholder="e.g. 25-1-00123-1" {...field} />
                    </div>
                  }
                />
              </div>

              <div className="row-primary">
                <h2>Job Location</h2>
                <div className="w-100">
                  <Controller
                    name="job_location_id"
                    control={control}
                    render={({ field }) => (
                      <div className="w-100">
                        <AsyncPaginate
                          menuPlacement="auto"
                          styles={{
                            ...selectCustomStyles,
                          }}
                          value={filterJobLocation}
                          loadOptions={filteredJobLocationLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(obj) => {
                            if (obj?.value) {
                              setFilterJobLocation(obj);
                              field.onChange(obj.value);
                            } else {
                              setFilterJobLocation(null);
                              field.onChange(null);
                            }
                          }}
                          additional={{
                            page: 1,
                          }}
                        />
                        {errors?.language_id?.message && (
                          <div style={{ marginBottom: 10 }}>
                            <ErrorMessage text={errors?.language_id?.message} />
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="row-primary">
                <h2>Language</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) =>
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        // Hold the chosen option (like Job Location) and only fall back to the
                        // languages slice: this page loads languages through the AsyncPaginate
                        // loader, which returns them instead of filling that slice, so the label
                        // used to disappear even while the filter was applied.
                        value={filterLanguage
                          ?? (field.value ? filteredLanguages?.find((opt) => field.value == opt.value) : "")}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          setFilterLanguage(obj || null);
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="submit" btnText="Search" className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} query={query} onQueryChange={applyQuery} />
            </div>
          </form>
        </div>

      </div>


      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;